import { FC } from 'react'
import { EducationFragment } from '../../../types'
import PerText from '../../../components/custom_components/Text'
import { Table } from 'antd'
import { useTranslation } from 'react-i18next'
import { useDateTime } from '../../../shared/useDateTime'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { PerTag } from '../../../components/custom_components/Tag'
import { TimeZoneAndLocale } from '../../../types.custom'
import { isColumnEmpty } from '../utils'

interface EducationTableProps {
  education: EducationFragment[]
  timezone: TimeZoneAndLocale
}

const isEducationFieldPopulated = (value: unknown): boolean => !!value

const hasAnyVisibleEducationField = (row: EducationFragment): boolean =>
  [row.university, row.officialDegreeName, row.level, row.title, row.grade, row.country, row.date].some(isEducationFieldPopulated)

export const EducationTable: FC<EducationTableProps> = ({ education, timezone }) => {
  const { t } = useTranslation()
  const { formatDateShort } = useDateTime(timezone)
  const dataSource = education.filter(hasAnyVisibleEducationField).map((education) => ({ ...education, key: education.id }))

  const educationColumns = [
    {
      key: 'education-university',
      title: t('profile.education.university'),
      className: 'minWidth-column-l',
      dataIndex: 'university',
      sorter: (universityA, universityB) => {
        const a = universityA.university ? universityA.university : ' '
        const b = universityB.university ? universityB.university : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex) => <PerText>{dataIndex ? dataIndex : null}</PerText>,
    },
    {
      key: 'education-degree',
      title: t('profile.education.degree'),
      className: 'minWidth-column-m',
      dataIndex: 'officialDegreeName',
      sorter: (degreeA, degreeB) => {
        const a = degreeA.officialDegreeName ? degreeA.officialDegreeName : ' '
        const b = degreeB.officialDegreeName ? degreeB.officialDegreeName : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex) => <PerText>{dataIndex ? dataIndex : null}</PerText>,
    },
    {
      key: 'education-level',
      title: t('profile.education.level.header'),
      className: 'minWidth-column-m',
      dataIndex: 'level',
      sorter: (levelA, levelB) => {
        const a = levelA.level ? levelA.level : ' '
        const b = levelB.level ? levelB.level : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex) => <PerText>{dataIndex ? t(`profile.education.level.${dataIndex}`) : null}</PerText>,
    },
    {
      key: 'education-title',
      title: t('profile.education.title'),
      className: 'minWidth-column-m',
      dataIndex: 'title',
      sorter: (titleA, titleB) => {
        const a = titleA.title ? titleA.title : ' '
        const b = titleB.title ? titleB.title : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex) => <PerText>{dataIndex ? dataIndex : null}</PerText>,
    },
    {
      key: 'education-grade',
      title: t('profile.education.grade'),
      className: 'minWidth-column-m',
      dataIndex: 'grade',
      sorter: (gradeA, gradeB) => {
        const a = gradeA.grade ? gradeA.grade : ' '
        const b = gradeB.grade ? gradeB.grade : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex) => (
        <PerText color="secondary" fontSize="xs">
          {dataIndex ? dataIndex : null}
        </PerText>
      ),
    },
    {
      key: 'education-country',
      title: t('profile.education.country'),
      className: 'minWidth-column-m',
      dataIndex: 'country',
      sorter: (countryA, countryB) => {
        const a = countryA.country ? countryA.country : ' '
        const b = countryB.country ? countryB.country : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex) => (
        <PerText color="secondary" fontSize="xs">
          {dataIndex ? t(`global.country.${dataIndex}`) : null}
        </PerText>
      ),
    },
    {
      key: 'education-date',
      title: t('profile.education.date'),
      className: 'minWidth-column-m',
      dataIndex: 'date',
      sorter: (dateA, dateB) => {
        const a = dateA.date ? dateA.date : ' '
        const b = dateB.date ? dateB.date : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex) => (
        <PerText color="secondary" fontSize="xs">
          {dataIndex ? formatDateShort(dataIndex, true) : null}
        </PerText>
      ),
    },
    {
      key: 'education-isHighestEducation',
      dataIndex: 'isHighestEducation',
      className: 'minWidth-column-s',
      render: (dataIndex) => <>{dataIndex && <PerTag color="green">{t('profile.education.isHighestEducation')}</PerTag>}</>,
    },
  ]

  // get non empty columns
  const filteredColumns = educationColumns.filter((column) => !isColumnEmpty(column, dataSource))
  // get empty columns to use it as padding at the end of a row
  const paddingColumns = educationColumns
    .filter((column) => isColumnEmpty(column, dataSource))
    .map((col) => {
      col.dataIndex = ''
      col.title = ''
      col.render = () => <></>
      col.sorter = undefined
      return col
    })

  return (
    <>
      <PerText appearance="tile-heading" mb="s">
        {t('profile.education.header')}
      </PerText>
      <Table
        dataSource={dataSource}
        columns={filteredColumns.concat(paddingColumns)}
        pagination={false}
        className="small"
        rowClassName={() => 'minHeight-row'}
        locale={{
          emptyText: <PerEmpty info={t('profile.education.emptyTable')} />,
        }}
      />
    </>
  )
}
